import React, { Component } from 'react';
import {Link} from 'react-router-dom';
import HttpRunner from '../query';
import urlHandler from '../../../utils/urlHandler';
import perms from '../../../utils/permissions';
import examples from '../../../utils/examples';
import miscUtils from '../../../utils/miscUtils';

class DocV1QueryParser extends Component {
    constructor(props){
        super(props);

        this.state = {
            dynamicSample: false
        }
    }

    componentDidMount() {
        this.setState({
            dynamicSample: perms.checkEndpoint(urlHandler.API_ENDPOINT_V1_QUERY_PARSER)
        })
    }

    render() {
        return (
            <div className="bucket" id="top">
                <span className='heading-1'>Query Parser</span>
                <code className="api-definition">
                    <span className="get-badge">GET</span>&nbsp;{urlHandler.API_ENDPOINT_V1_QUERY_PARSER}?{'{'}query parameters{'}'}
                </code>
                <div className="description">
                    The Query Parser API allows you to turn keyword search queries into geospatial search queries. Query Parser 
                    analyzes an unstructured query string, identifies the most prominent geographic place and resolves the place 
                    to Earth coordinates. The output includes a location object for the geospatial component  of the query as well 
                    as the remaining keywords of the query. This enables a more intelligent query that includes both a keyword 
                    search and a geographic filter.
                </div>

                <h4 className="heading-2" id="qpv1_request">Request</h4>
                <div className="description enrich">
                    You can request a parsed query from the Query Parser API by constructing a URL including any of the parameters 
                    specified in the request parameter list below.<br/>
                </div>

                <h4 className="heading-3" id="qpv1_request_params">Query parameters</h4>

                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Default </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">query</td>
                        <td className="meaning">Input for which a Query Parser search is initiated.
                        </td>
                        <td className="type">String</td>
                        <td className="constraints">Required.</td>
                    </tr>
                    <tr>
                        <td className="attribute">outputVersion</td>
                        <td className="meaning">Output version format.
                        </td>
                        <td className="type">String<br/>
                            <br/>
                            Possible values (2.2.0, 4.0.0)<br/>
                            <br/>
                            NOTE: 2.2.0 is for backward compatability.
                        </td>
                        <td className="constraints">Optional.<br/>
                            Default: 4.0.0
                        </td>
                    </tr>
                    <tr>
                        <td className="attribute">bbox</td>
                        <td className="meaning">A comma separated coordinate list (minlon,minlat,maxlon,maxlat) that defines 
                            the query’s geographic extent.<br/>
                            <br/>
                            Results outside this extent are ignored. If this is not specified, 
                            the bounding box contains the whole globe.
                        </td>
                        <td className="type">String</td>
                        <td className="constraints">Optional.<br/>
                            Default: -180,-90,180,90</td>
                    </tr>
                    <tr>
                        <td className="attribute">geometry</td>
                        <td className="meaning">A string expressed as WKT (see {miscUtils.createExternalLink('http://docs.opengeospatial.org/is/12-063r5/12-063r5.html')}) sets the geometry format 
                            for any locations returned.
                        </td>
                        <td className="type">String<br/>
                            <br/>
                            Possible values ("WKT")<br/>
                        </td>
                        <td className="constraints">Optional.</td>
                    </tr>
                    <tr>
                        <td className="attribute">minGeoConfidence</td>
                        <td className="meaning">Geographic places with a confidence lower than minGeoConfidence, will not be returned.
                        </td>
                        <td className="type">Float<br/>
                            <br/>
                            Must be &gt;= 0 and &lt;= 1.
                        </td>
                        <td className="constraints">Optional.</td>
                    </tr>
                    <tr>
                        <td className="attribute">includeCustomField</td>
                        <td className="meaning">If includeCustomField is true, then system will return any user-defined custom fields 
                            which match the regular expression provided.<br/>
                            <br/>
                            NOTE: This only applies to on-premise deployments with custom Gazetteer.
                        </td>
                        <td className="type">Boolean</td>
                        <td className="constraints">Optional.</td>
                    </tr>
                    <tr>
                        <td className="attribute">customFieldRegex</td>
                        <td className="meaning">The regular expression will only be applied if includeCustomField is set to 
                            true.<br/>
                            <br/>
                            The system will return any user-defined custom fields which match the regular expression and 
                            with higher confidence.<br/>
                            <br/>
                            NOTE: This only applies to on-premise deployments with custom Gazetteer.
                        </td>
                        <td className="type">String</td>
                        <td className="constraints">Optional.</td>
                    </tr>
                    </tbody>
                </table>

                <h4 className="heading-2" id="qpv1_response">Response</h4>
                <div className="description enrich">
                    Query Parser returns at most one location.
                </div>

                <h4 className="heading-3" id="qpv1_response_object">Response object</h4>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Notes </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">location</td>
                        <td className="meaning">Location object corresponding to the tagged geographic word or phrase 
                            from the input query.
                        </td>
                        <td className="type">See <a href="#qpv1_location_object">location object</a> description</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">remainingQuery</td>
                        <td className="meaning">Non-geographic portion of the input query.
                        </td>
                        <td className="type">String</td>
                        <td className="type"></td>
                    </tr>
                    </tbody>
                </table>

                <h4 className="heading-4" id="qpv1_location_object">Location object</h4>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Notes </th>
                    </tr>
                    </thead>
                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">id</td>
                        <td className="meaning">The knowledgebase globally unique identifier.</td>
                        <td className="type">Integer</td>
                        <td className="notes"></td>
                    </tr>
                    <tr>
                        <td className="attribute">type</td>
                        <td className="meaning">The type of entity.</td>
                        <td className="type">String</td>
                        <td className="notes"><i>GEO</i> for location.</td>
                    </tr>
                    <tr>
                        <td className="attribute">label</td>
                        <td className="meaning">A finch canonical name for the entity.</td>
                        <td className="type">String</td>
                        <td className="notes"></td>
                    </tr>
                    <tr>
                        <td className="attribute">latitude</td>
                        <td className="meaning">The latitude of the geographic location.</td>
                        <td className="type">Float</td>
                        <td className="notes"></td>
                    </tr>
                    <tr>
                        <td className="attribute">longitude</td>
                        <td className="meaning">The longitude of the geographic location.</td>
                        <td className="type">Float</td>
                        <td className="notes"></td>
                    </tr>
                    <tr>
                        <td className="attribute">boundingBox</td>
                        <td className="meaning">The geographic location's bounding box.</td>
                        <td className="type">See <a href="#qpv1_bb_object">boundingBox object</a> description</td>
                        <td className="notes"></td>
                    </tr>
                    <tr>
                        <td className="attribute">population</td>
                        <td className="meaning">The population of the geographic location.</td>
                        <td className="type">Integer</td>
                        <td className="notes"></td>
                    </tr>
                    <tr>
                        <td className="attribute">dsgFeatureClass</td>
                        <td className="meaning">The DSG feature class of the geographic location. Specified as a single capitalized letter.</td>
                        <td className="type">String</td>
                        <td className="notes">See <Link to="/dsg_classes">DSG feature classes</Link>.</td>
                    </tr>
                    <tr>
                        <td className="attribute">dsgFeatureClassLabel</td>
                        <td className="meaning">The DSG feature class label. Omit if not available.</td>
                        <td className="type">String</td>
                        <td className="notes"></td>
                    </tr>  
                    <tr>
                        <td className="attribute">dsgFeatureCode</td>
                        <td className="meaning">The DSG feature code of the geographic location. A two to five-character code used to identify the type of feature a name is applied to.</td>
                        <td className="type">String</td>
                        <td className="notes">See <Link to="/dsg_codes">DSG feature codes</Link>.</td>
                    </tr>    
                    <tr>
                        <td className="attribute">dsgFeatureCodeLabel</td>
                        <td className="meaning">The DSG feature code display name.</td>
                        <td className="type">String</td>
                        <td className="notes"></td>
                    </tr> 
                    <tr>
                        <td className="attribute">dsgFeatureCodeDescription</td>
                        <td className="meaning">The DSG feature code description.</td>
                        <td className="type">String</td>
                        <td className="notes"></td>
                    </tr>   
                    <tr>
                        <td className="attribute">countryLabel</td>
                        <td className="meaning">The Finch canonical name for the country.</td>
                        <td className="type">String</td>
                        <td className="notes"></td>
                    </tr>   
                    <tr>
                        <td className="attribute">countryFips</td>
                        <td className="meaning">The two-character FIPS country code.</td>
                        <td className="type">String</td>
                        <td className="notes">See <Link to="/fips_codes">FIPS country codes</Link>.</td>
                    </tr>     
                    <tr>
                        <td className="attribute">countryGenc2</td>
                        <td className="meaning">GENC country digraph for the location.</td>
                        <td className="type">String</td>
                        <td className="notes">See <Link to="/genc_codes">GENC country digraphs</Link>.</td>
                    </tr>   
                    <tr>
                        <td className="attribute">countryGenc3</td>
                        <td className="meaning">GENC country trigraph for the location.</td>
                        <td className="type">String</td>
                        <td className="notes">See <Link to="/genc_codes">GENC country trigraphs</Link>.</td>
                    </tr>  
                    <tr>
                        <td className="attribute">countryGencCode</td>
                        <td className="meaning">GENC country numeric code for the location.</td>
                        <td className="type">Integer</td>
                        <td className="notes">See <Link to="/genc_codes">GENC country numeric codes</Link>.</td>
                    </tr>  
                    <tr>
                        <td className="attribute">countryDsgFeatureCode</td>
                        <td className="meaning">The DSG feature code of the geographic location's country. A two to five-character code used to identify the type of feature a name is applied to.</td>
                        <td className="type">String</td>
                        <td className="notes">See <Link to="/dsg_codes">DSG feature codes</Link>.</td>
                    </tr>  
                    <tr>
                        <td className="attribute">admin1Label</td>
                        <td className="meaning">The Finch canonical name for the country's first administration level.</td>
                        <td className="type">String</td>
                        <td className="notes"></td>
                    </tr>
                    <tr>
                        <td className="attribute">admin1Fips</td>
                        <td className="meaning">The four-character FIPS region code.</td>
                        <td className="type">String</td>
                        <td className="notes">See <Link to="/fips_codes">FIPS region codes</Link>.</td>
                    </tr>
                    <tr>
                        <td className="attribute">admin1Genc</td>
                        <td className="meaning">GENC province code for the location.</td>
                        <td className="type">String</td>
                        <td className="notes">See <Link to="/genc_codes">GENC province codes</Link>.</td>
                    </tr>
                    <tr>
                        <td className="attribute">admin1DsgFeatureCode</td>
                        <td className="meaning">The DSG feature code of the geographic location's first administration level. A two to five-character code used to identify the type of feature a name is applied to.</td>
                        <td className="type">String</td>
                        <td className="notes">See <Link to="/dsg_codes">DSG feature codes</Link>.</td>
                    </tr>
                    <tr>
                        <td className="attribute">admin2Label</td>
                        <td className="meaning">The Finch canonical name for the country's secondary administration level</td>
                        <td className="type">String</td>
                        <td className="notes"></td>
                    </tr>
                    <tr>
                        <td className="attribute">admin2DsgFeatureCode</td>
                        <td className="meaning">The DSG feature code of the geographic location's second administration level. A two to five-character code used to identify the type of feature a name is applied to.</td>
                        <td className="type">String</td>
                        <td className="notes">See <Link to="/dsg_codes">DSG feature codes</Link>.</td>
                    </tr>

                    <tr>
                        <td className="attribute">boundingBoxDerivation</td>
                        <td className="meaning">Whether an enclosing geometry (polygon) is available.</td>
                        <td className="type">String<br/>
                            <br/>
                            Possible values ("Estimate", "Geometry")<br/>
                            <br/>
                            Estimate: When no enclosing geometry (polygon) available<br/>
                            <br/>
                            Geometry: When enclosing geometry (polygon) is available
                        </td>
                        <td className="notes"></td>
                    </tr>
                    <tr>
                        <td className="attribute">extractedText</td>
                        <td className="meaning">The tagged geographic word or phrase considered in the interpretation of the input query.</td>
                        <td className="type">String</td>
                        <td className="notes"></td>
                    </tr>
                    <tr>
                        <td className="attribute">fullName</td>
                        <td className="meaning">The full name of the location (includes administrative hierarchy of the location).</td>
                        <td className="type">String</td>
                        <td className="notes"></td>
                    </tr>
                    <tr>
                        <td className="attribute">locationScore</td>
                        <td className="meaning">The probability that the “extractedText” intends to refer to this location.</td>
                        <td className="type">Float</td>
                        <td className="notes"></td>
                    </tr>
                    <tr>
                        <td className="attribute">queryConfidence</td>
                        <td className="meaning">Probability that this is the correct interpretation of the query string in the request.
                        </td>
                        <td className="type">Float</td>
                        <td className="notes"></td>
                    </tr>
                    </tbody>
                </table>

                <h4 className="heading-4" id="qpv1_bb_object">BoundingBox object</h4>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                    </tr>
                    </thead>
                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">minLatitude</td>
                        <td className="meaning">The geographic location's minimum latitude bounding box.</td>
                        <td className="type">Float</td>
                    </tr>
                    <tr>
                        <td className="attribute">maxLatitude</td>
                        <td className="meaning">The geographic location's maximum latitude bounding box.</td>
                        <td className="type">Float</td>
                    </tr>
                    <tr>
                        <td className="attribute">minLongitude</td>
                        <td className="meaning">The geographic location's minimum longitude bounding box.</td>
                        <td className="type">Float</td>
                    </tr>
                    <tr>
                        <td className="attribute">maxLongitude</td>
                        <td className="meaning">The geographic location's maximum longitude bounding box.</td>
                        <td className="type">Float</td>
                    </tr>
                    </tbody>
                </table>

                <h4 className="heading-2" id="qpv1_response_object">Example</h4>
                <div className="example-content host">
                    <pre>
                        GET {urlHandler.API_ENDPOINT_V1_QUERY_PARSER}<br/>
                        Accept: application/json<br/>
                        Authorization: Bearer [YOUR-API-KEY]<br/>
                        <br/>
                    </pre>
                    <pre>http://example.com/api/v1/query-parser?query=museums in Dayton&minGeoConfidence=0.4</pre>
                </div>
                <HttpRunner query="query-parser" 
                    stated="query=museums in Dayton&minGeoConfidence=0.4"
                    response={!this.state.dynamicSample ? JSON.stringify(examples.EXAMPLE_RESPONSE_V1_QUERY_PARSER) : null}
                />
            </div>
        );
    }
}

export default DocV1QueryParser;